import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { Session, SupabaseClient, User } from "@supabase/supabase-js";
import type { Profile } from "@/lib/supabase/types";
import { recordLogin } from "@/lib/auth/record-login";

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  isLoading: boolean;
  /** Re-read the profile row, e.g. after the welcome tour marks itself done. */
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Loaded on first use rather than imported, so supabase-js (the largest
// dependency) stays out of the bundle that paints /go. The provider mounts on
// every route, so the client is still created on load, just after first paint.
let clientPromise: Promise<SupabaseClient> | null = null;
function getClient(): Promise<SupabaseClient> {
  clientPromise ??= import("@/lib/supabase/client").then((m) => m.createClient());
  return clientPromise;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchProfile = useCallback(async (userId: string) => {
    const supabase = await getClient();
    const { data } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .single();

    setProfile((data as unknown as Profile) ?? null);
  }, []);

  // Telemetry only: a failed write must never block or break sign-in.
  const noteSignIn = useCallback((supabase: SupabaseClient, s: Session | null) => {
    if (!s?.user) return;
    recordLogin(supabase, s.user).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    getClient().then((supabase) => {
      if (cancelled) return;

      // Hydrate session on mount
      supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
        if (cancelled) return;
        setSession(initialSession);
        noteSignIn(supabase, initialSession);

        if (initialSession?.user) {
          fetchProfile(initialSession.user.id).finally(() => setIsLoading(false));
        } else {
          setIsLoading(false);
        }
      });

      // Listen for auth state changes
      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((_event, newSession) => {
        setSession(newSession);
        noteSignIn(supabase, newSession);

        if (newSession?.user) {
          fetchProfile(newSession.user.id);
        } else {
          setProfile(null);
        }
      });
      unsubscribe = () => subscription.unsubscribe();
    });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [fetchProfile, noteSignIn]);

  const user = session?.user ?? null;

  const refreshProfile = useCallback(async () => {
    if (user) await fetchProfile(user.id);
  }, [user, fetchProfile]);

  const value = useMemo(
    () => ({ user, session, profile, isLoading, refreshProfile }),
    [user, session, profile, isLoading, refreshProfile]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
